import { useMemo } from 'react'
import { INFRA_MAINTENANCE_KINDS, INFRA_MAINTENANCE_NOTE_MAX, type InfraMaintenanceKind, type InfraRecordMaintenanceV1 } from '@gk/contracts'
import { useMerged } from '../sync/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import { isOpen } from '../sync/projectors.ts'
import type { GeoJsonGeometry } from '../lib/geo.ts'
import type { PlanJob } from './plan.ts'
import type { FaultRow } from './water.ts'

export interface AreaRow {
  id: string; parent_id: string | null; area_type_code: string; code: string | null; name: { fr?: string; en?: string }
  hole_number: number | null; sort_key: number; archived_at: string | null; revision: number
}
export interface AreaVersionRow {
  id: string; area_id: string; version_no: number; geom: GeoJsonGeometry; area_m2: number; source: string; accuracy_m: number | null
  captured_on: string; note: string | null; valid_during: string
}
export interface InfraRow {
  id: string; kind: string; label: string; code: string | null; area_id: string | null; note: string | null
  retired_at: string | null; retired_reason: string | null; revision: number
}
export interface InfraVersionRow {
  id: string; feature_id: string; version_no: number; geom: GeoJsonGeometry; length_m: number | null; source: string; accuracy_m: number | null
  depth_m: number | null; captured_on: string; note: string | null; valid_during: string
}
export interface MapLayerRow {
  id: string; kind: 'ortho_pmtiles' | 'cadastre'; version: number; file_id: string; storage_key: string; sha256: string; bytes: number; source: string
  capture_year: number | null; resolution_m: number | null; attribution: string; min_zoom: number; max_zoom: number; bounds: number[] | null; created_at: string
}

export const useAreas = () => useMerged<AreaRow>('area', 'geo.area')
export const useAreaVersions = () => useMerged<AreaVersionRow>('area_version', 'geo.area_version')
export const useInfra = () => useMerged<InfraRow>('infra_feature', 'geo.infra_feature')
export const useInfraVersions = () => useMerged<InfraVersionRow>('infra_version', 'geo.infra_feature_version')
export const useMapLayers = () => useMerged<MapLayerRow>('map_layer', 'geo.map_layer')

/** The label of an area in the user's language, falling back to the other one and then to the code: a club names its own areas. */
export function areaLabel(a: Pick<AreaRow, 'name' | 'code'>, lang: string): string {
  const l = lang.startsWith('en') ? 'en' : 'fr'
  return a.name[l] ?? a.name.fr ?? a.name.en ?? a.code ?? ''
}

export interface AreaNode { area: Pending<AreaRow>; children: AreaNode[]; version: AreaVersionRow | undefined; depth: number }

/** The tree under the site root, siblings by sort key then by label, archived areas left out unless asked for. */
export function buildAreaTree(areas: Array<Pending<AreaRow>>, versions: AreaVersionRow[], lang: string, includeArchived = false): AreaNode[] {
  const current = new Map<string, AreaVersionRow>()
  for (const v of versions) if (isOpen(v)) current.set(v.area_id, v)
  const kids = new Map<string | null, Array<Pending<AreaRow>>>()
  for (const a of areas) {
    if (a.archived_at && !includeArchived) continue
    const list = kids.get(a.parent_id) ?? []
    list.push(a); kids.set(a.parent_id, list)
  }
  const make = (a: Pending<AreaRow>, depth: number): AreaNode => ({
    area: a, version: current.get(a.id), depth,
    children: (kids.get(a.id) ?? []).sort((x, y) => x.sort_key - y.sort_key || areaLabel(x, lang).localeCompare(areaLabel(y, lang), lang)).map((c) => make(c, depth + 1)),
  })
  return (kids.get(null) ?? []).map((r) => make(r, 0))
}

export interface FeatureCollectionOf { type: 'FeatureCollection'; features: Array<{ type: 'Feature'; id: string; geometry: GeoJsonGeometry; properties: Record<string, unknown> }> }

/** Areas as one GeoJSON collection for the map: the shape in force, with what the legend and the info sheet need. */
export function areaFeatures(areas: Array<Pending<AreaRow>>, versions: AreaVersionRow[], lang: string): FeatureCollectionOf {
  const byArea = new Map<string, AreaVersionRow>()
  for (const v of versions) if (isOpen(v)) byArea.set(v.area_id, v)
  const features: FeatureCollectionOf['features'] = []
  for (const a of areas) {
    const v = byArea.get(a.id)
    if (!v || a.archived_at) continue
    features.push({
      type: 'Feature', id: a.id, geometry: v.geom,
      properties: { id: a.id, type: a.area_type_code, label: areaLabel(a, lang), code: a.code ?? '', hole: a.hole_number ?? 0, area_m2: Number(v.area_m2), source: v.source, accuracy_m: v.accuracy_m ?? -1, version_no: v.version_no },
    })
  }
  return { type: 'FeatureCollection', features }
}

/** Infrastructure as one GeoJSON collection (retired items are left out). */
export function infraFeatures(items: Array<Pending<InfraRow>>, versions: InfraVersionRow[]): FeatureCollectionOf {
  const byFeature = new Map<string, InfraVersionRow>()
  for (const v of versions) if (isOpen(v)) byFeature.set(v.feature_id, v)
  const features: FeatureCollectionOf['features'] = []
  for (const f of items) {
    const v = byFeature.get(f.id)
    if (!v || f.retired_at) continue
    features.push({
      type: 'Feature', id: f.id, geometry: v.geom,
      properties: { id: f.id, kind: f.kind, label: f.label, code: f.code ?? '', source: v.source, accuracy_m: v.accuracy_m ?? -1, depth_m: v.depth_m ?? -1, length_m: v.length_m ?? -1, line: /Line/.test(v.geom.type) },
    })
  }
  return { type: 'FeatureCollection', features }
}

/** Everything the course and map screens need, memoised; `ready` is false until the local store has answered. */
export function useCourse(lang: string) {
  const areas = useAreas(); const versions = useAreaVersions(); const infra = useInfra(); const infraVersions = useInfraVersions()
  return useMemo(() => {
    const ready = !!areas && !!versions && !!infra && !!infraVersions
    const a = areas ?? [], av = versions ?? [], f = infra ?? [], fv = infraVersions ?? []
    return {
      ready, areas: a, areaVersions: av, infra: f, infraVersions: fv,
      tree: buildAreaTree(a, av, lang), areaCollection: areaFeatures(a, av, lang), infraCollection: infraFeatures(f, fv),
      historyOf: (areaId: string) => av.filter((v) => v.area_id === areaId).sort((x, y) => y.version_no - x.version_no),
    }
  }, [areas, versions, infra, infraVersions, lang])
}

// ---- M16-E: the feature's own history, one panel on the map (docs/M16.md E) -----------------------------------------------------------------------------------
/** What was done on a feature (`geo.infra_maintenance`, a ledger mirrored on both profiles; Dexie v15). */
export interface InfraMaintenanceRow {
  id: string; feature_id: string; done_on: string; kind: InfraMaintenanceKind; note: string | null; job_id: string | null; work_order_id: string | null
  entered_at: string; actor_person_id?: string | null
}
export const useInfraMaintenance = () => useMerged<InfraMaintenanceRow>('infra_maintenance', 'geo.infra_maintenance')

/** A feature's maintenance, newest first: the day it was done, then when it was entered, then the id (two records of one moment keep one order). */
export function maintenanceOf<R extends InfraMaintenanceRow>(rows: readonly R[], featureId: string): R[] {
  return rows.filter((r) => r.feature_id === featureId).sort((a, b) => b.done_on.localeCompare(a.done_on) || b.entered_at.localeCompare(a.entered_at) || b.id.localeCompare(a.id))
}
/** A feature's positions, the one in force first, then the older ones (a shape is a version, never an edit: M6). */
export function infraVersionsOf<V extends Pick<InfraVersionRow, 'feature_id' | 'version_no'>>(versions: readonly V[], featureId: string): V[] {
  return versions.filter((v) => v.feature_id === featureId).sort((a, b) => b.version_no - a.version_no)
}
/** The irrigation faults raised on a station (`ops.irrigation_fault.station_feature_id`): the open ones first, oldest first (the longest waiting), then the
 *  resolved ones, the latest resolution first. */
export function faultsAt<F extends Pick<FaultRow, 'id' | 'station_feature_id' | 'observed_at' | 'resolved_at'>>(faults: readonly F[], featureId: string): F[] {
  const here = faults.filter((f) => f.station_feature_id === featureId)
  const open = here.filter((f) => !f.resolved_at).sort((a, b) => a.observed_at.localeCompare(b.observed_at) || a.id.localeCompare(b.id))
  const closed = here.filter((f) => f.resolved_at).sort((a, b) => (b.resolved_at ?? '').localeCompare(a.resolved_at ?? '') || b.id.localeCompare(a.id))
  return [...open, ...closed]
}
/** The photos of a feature (`attachment_link`, entity 'geo.infra_feature'; M17's portrait pattern): newest first, a hidden one left out. The newest is the one the
 *  panel shows large; the older ones stay (a photo is never deleted). */
export const INFRA_PHOTO_ENTITY = 'geo.infra_feature' as const
export const INFRA_PHOTO_PURPOSE = 'photo'
export function infraPhotosOf<L extends { id: string; entity_type: string; entity_id: string; captured_at: string | null; hidden_at: string | null }>(links: readonly L[], featureId: string): L[] {
  return links.filter((l) => l.entity_type === INFRA_PHOTO_ENTITY && l.entity_id === featureId && !l.hidden_at)
    .sort((a, b) => (b.captured_at ?? '').localeCompare(a.captured_at ?? '') || b.id.localeCompare(a.id))
}

/** "Entretien fait": the form's rule, said next to a disabled button (M23 item 6). The server holds the same bounds (a day at most one after the club's today, none
 *  before 2000, a note of 300 code points); a note of spaces is no note. */
export type MaintenanceProblem = 'kind' | 'date' | 'future' | 'past' | 'noteLong'
export const MAINTENANCE_EARLIEST = '2000-01-01'
/** YYYY-MM-DD naming a day that exists (2026-02-30 does not): the server refuses the rest as PAYLOAD_INVALID. */
export function isCalendarDay(day: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!m) return false
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3])
  return mo >= 1 && mo <= 12 && d >= 1 && d <= new Date(Date.UTC(y, mo, 0)).getUTCDate()
}
export function maintenanceProblem(f: { kind: string; doneOn: string; note: string }, today: string): MaintenanceProblem | null {
  if (!(INFRA_MAINTENANCE_KINDS as readonly string[]).includes(f.kind)) return 'kind'
  if (!isCalendarDay(f.doneOn)) return 'date'
  if (f.doneOn > today) return 'future'
  if (f.doneOn < MAINTENANCE_EARLIEST) return 'past'
  if ([...f.note.trim()].length > INFRA_MAINTENANCE_NOTE_MAX) return 'noteLong'
  return null
}
/** The payload the form sends: the day always (what the person saw), the note trimmed or nothing, the job when one was picked. */
export function buildMaintenancePayload(id: string, featureId: string, f: { kind: InfraMaintenanceKind; doneOn: string; note: string; jobId?: string | null }): InfraRecordMaintenanceV1 {
  const note = f.note.trim()
  return { maintenance_id: id, feature_id: featureId, kind: f.kind, done_on: f.doneOn, ...(note ? { note } : {}), ...(f.jobId ? { job_id: f.jobId } : {}) }
}
/** The jobs a maintenance may name: that day's live jobs the person is on or that cover the feature's area, the area's first, then the board's order. */
export function maintenanceJobOptions<J extends Pick<PlanJob, 'id' | 'state' | 'scheduled_local_date' | 'areaIds' | 'title_text'> & { crew: ReadonlyArray<{ person_id: string }> }>(
  jobs: readonly J[], day: string, areaId: string | null, personId: string | null): J[] {
  const onArea = (j: J) => !!areaId && j.areaIds.includes(areaId)
  return jobs.filter((j) => j.scheduled_local_date === day && j.state !== 'cancelled' && (onArea(j) || (!!personId && j.crew.some((c) => c.person_id === personId))))
    .sort((a, b) => Number(onArea(b)) - Number(onArea(a)) || a.title_text.localeCompare(b.title_text) || a.id.localeCompare(b.id))
}
